import { useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import { useAuth } from '../../context/AuthContext';
import { useAsync } from '../../lib/useAsync';
import { newsApi } from '../../api/resources';
import { StatusBadge, PageLoader, ErrorState, EmptyState } from '../../components/ui';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../lib/format';
import SubmissionPreview from '../../components/SubmissionPreview';
import { normalizeSubmission } from '../../components/submissionTypes';

export default function MyNews() {
  const { user } = useAuth();
  const toast = useToast();
  // Members see published news + their OWN drafts from this endpoint; filter to own.
  const { data, loading, error, reload } = useAsync(() => newsApi.list({ per_page: 50 }), []);
  // Read-only "how it will look published" preview, any status.
  const [preview, setPreview] = useState(null);

  const mine = (data?.items || []).filter((n) => n.author?.id === user?.id);

  async function handleSubmit(id) {
    try {
      await newsApi.submit(id, 'Submitted for review.');
      toast.success('News submitted for review.');
      reload();
    } catch (err) {
      toast.error(err.message || 'Could not submit.');
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this news item? This cannot be undone.')) return;
    try {
      await newsApi.remove(id);
      toast.success('News deleted.');
      reload();
    } catch (err) {
      toast.error(err.message || 'Could not delete.');
    }
  }

  if (loading) return <PageLoader />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <div className="space-y-6">
      <Seo title="My News" />
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold">My News</h1>
        <Link to="/member/news/new" className="btn-primary">
          + New News
        </Link>
      </div>

      <p className="text-sm text-ink-muted">
        Every news item you have written, from drafts to published. Use Preview to see exactly how a
        post looks before or after it goes live.
      </p>

      {mine.length === 0 ? (
        <EmptyState
          title="No news yet"
          message="Share an area update by writing your first news item."
          icon="📰"
          action={
            <Link to="/member/news/new" className="btn-primary">
              Write news
            </Link>
          }
        />
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-cream-300 bg-surface-alt text-xs uppercase tracking-wider text-ink-soft">
              <tr>
                <th className="px-5 py-3">Title</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Updated</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-300">
              {mine.map((n) => (
                <tr key={n.id} className="hover:bg-surface-alt">
                  <td className="px-5 py-3 font-medium text-ink">{n.title}</td>
                  <td className="px-5 py-3">
                    <StatusBadge status={n.status} />
                  </td>
                  <td className="px-5 py-3 text-ink-muted">{formatDate(n.updated_at || n.created_at)}</td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setPreview(normalizeSubmission(n, 'news'))}
                        className="btn-outline px-3 py-1.5 text-xs"
                        aria-label={`Preview ${n.title}`}
                      >
                        Preview
                      </button>
                      {n.is_published ? (
                        <a
                          href={`/news/${n.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-ghost px-3 py-1.5 text-xs"
                        >
                          View
                        </a>
                      ) : (
                        <Link to={`/member/news/${n.id}/edit`} className="btn-ghost px-3 py-1.5 text-xs">
                          Edit
                        </Link>
                      )}
                      {(n.status === 'draft' || n.status === 'rejected') && (
                        <button onClick={() => handleSubmit(n.id)} className="btn-gold px-3 py-1.5 text-xs">
                          Submit
                        </button>
                      )}
                      {!n.is_published && (
                        <button
                          onClick={() => handleDelete(n.id)}
                          className="btn-ghost px-3 py-1.5 text-xs text-red-600"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SubmissionPreview item={preview} onClose={() => setPreview(null)} readOnly />
    </div>
  );
}
